import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { v4 as uuid } from "uuid";
import ErrorHandler from "./ErrorHandler";
import { CustomError } from "../model/internal/customError";
import { logUnhandledException } from "../services/apiService";
import { useAuth } from "../context/AuthContext";

interface UnhandledExceptionRequestDto {
  ErrorId: string;
  ErrorUtc: string;
  Message: string;
  Stack: string;
  ComponentStack: string;
}

// Higher Order Component to implement error boundary
const CustomErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken } = useAuth();

  const addCustomProperties = (error: CustomError) => {
    error.errorId ??= uuid();
    error.errorUtc ??= new Date().toISOString();
  };

  const logError = async (error: Error, info: { componentStack: string }) => {
    const customError = error as CustomError;
    try {
      addCustomProperties(customError);

      console.error(
        `Unhandled error: Utc ${customError.errorUtc} Id ${customError.errorId}`,
        error,
        info,
      );

      const token = await getToken();

      const dto: UnhandledExceptionRequestDto = {
        ErrorId: customError.errorId ?? "",
        ErrorUtc: customError.errorUtc ?? "",
        Message: customError.message ?? "",
        Stack: customError.stack ?? "",
        ComponentStack: info.componentStack ?? "",
      };
      await logUnhandledException(token, dto);
    } catch (e) {
      console.error("Logging error", e);
    }
  };

  const FallbackComponent = ({ error, resetErrorBoundary }: any) => {
    const customError = error as CustomError;
    addCustomProperties(customError);

    return <ErrorHandler customError={customError} resetErrorBoundary={resetErrorBoundary} />;
  };

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={logError}>
      {children}
    </ErrorBoundary>
  );
};

export default CustomErrorBoundary;
